"use client";

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from "react";

// ============================================================================
// Prompt Dialog Hook
// ============================================================================
// SSR-safe prompt dialog that replaces window.prompt().
//
// Usage:
//   import { usePrompt } from "@/components/feedback/usePrompt";
//   const prompt = usePrompt();
//   
//   const handleRename = async () => {
//     const newName = await prompt({
//       title: "Rename Item",
//       message: "Enter a new name for this item:",
//       placeholder: "New name",
//       defaultValue: currentName,
//       confirmText: "Rename",
//       cancelText: "Cancel",
//     });
//     if (newName !== null) {
//       // newName is the entered value (can be empty string if user submitted empty)
//     }
//   };
// ============================================================================

export interface PromptOptions {
  title: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  validate?: (value: string) => string | null; // Returns error message or null if valid
}

interface PromptContextValue {
  prompt: (options: PromptOptions) => Promise<string | null>;
}

interface PromptState extends PromptOptions {
  isOpen: boolean;
  resolve: ((value: string | null) => void) | null;
}

const PromptContext = createContext<PromptContextValue | null>(null);

export function usePrompt(): (options: PromptOptions) => Promise<string | null> {
  const context = useContext(PromptContext);
  if (!context) {
    throw new Error("usePrompt must be used within a PromptProvider");
  }
  return context.prompt;
}

export function PromptProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PromptState>({
    isOpen: false,
    title: "",
    message: "",
    placeholder: "",
    defaultValue: "",
    confirmText: "OK",
    cancelText: "Cancel",
    resolve: null,
  });

  const prompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        title: options.title,
        message: options.message,
        placeholder: options.placeholder || "",
        defaultValue: options.defaultValue || "",
        confirmText: options.confirmText || "OK",
        cancelText: options.cancelText || "Cancel",
        validate: options.validate,
        resolve,
      });
    });
  }, []);

  const handleSubmit = useCallback((value: string) => {
    state.resolve?.(value);
    setState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [state.resolve]);

  const handleCancel = useCallback(() => {
    state.resolve?.(null);
    setState((prev) => ({ ...prev, isOpen: false, resolve: null }));
  }, [state.resolve]);

  return (
    <PromptContext.Provider value={{ prompt }}>
      {children}
      <PromptDialog
        isOpen={state.isOpen}
        title={state.title}
        message={state.message}
        placeholder={state.placeholder}
        defaultValue={state.defaultValue}
        confirmText={state.confirmText}
        cancelText={state.cancelText}
        validate={state.validate}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </PromptContext.Provider>
  );
}

// Prompt dialog component
interface PromptDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  validate?: (value: string) => string | null;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

function PromptDialog({
  isOpen,
  title,
  message,
  placeholder = "",
  defaultValue = "",
  confirmText = "OK",
  cancelText = "Cancel",
  validate,
  onSubmit,
  onCancel,
}: PromptDialogProps) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset value when dialog opens
  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
      setError(null);
      // Focus input after a short delay to ensure it's rendered
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, defaultValue]);

  const handleSubmit = () => {
    if (validate) {
      const validationError = validate(value);
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    onSubmit(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 dark:bg-black/70">
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4 border border-gray-200 dark:border-gray-700 animate-scale-in"
        role="dialog"
        aria-modal="true"
        aria-labelledby="prompt-dialog-title"
        aria-describedby="prompt-dialog-message"
      >
        <h3 
          id="prompt-dialog-title"
          className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"
        >
          {title}
        </h3>
        
        <div 
          id="prompt-dialog-message"
          className="text-sm text-gray-600 dark:text-gray-400 mb-4 whitespace-pre-line"
        >
          {message}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`w-full px-3 py-2 border rounded-md text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 mb-2 ${
            error 
              ? "border-red-500 dark:border-red-500" 
              : "border-gray-300 dark:border-gray-600"
          }`}
        />

        {error && (
          <p className="text-xs text-red-500 mb-4">{error}</p>
        )}

        <div className="flex gap-3 justify-end mt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-md transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
